import { createPortal } from 'react-dom'
import WebApp from '@twa-dev/sdk'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Share2, Star } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api, FeedbackPayload, TodayData } from '../api/client'
import { useTelegram } from '../hooks/useTelegram'

const RATING_LS_KEY = 'rating_shown'
const RATING_DAYS_THRESHOLD = 7

const SHARE_TEXT =
  'Попробуй Delo — личный ассистент в Telegram. Помогаю с делами и вопросами, ' +
  'запоминаю важное, напоминаю вовремя — голосом или текстом.'

/**
 * Опрос «как тебе Delo» — показывается один раз, на 7-й день после регистрации.
 * Живёт на главном экране (Календарь): именно туда пользователь заходит регулярно.
 */
export function RatingPrompt() {
  const { haptic } = useTelegram()

  const [show, setShow] = useState(false)
  const [rating, setRating] = useState(0)
  const [text, setText] = useState('')
  const [done, setDone] = useState(false)
  const checked = useRef(false)

  // Порог показа считаем по days_since_registration из /api/today.
  // Запрос не делаем вовсе, если опрос уже был показан.
  const alreadyShown = typeof localStorage !== 'undefined' && !!localStorage.getItem(RATING_LS_KEY)

  const { data } = useQuery<TodayData>({
    queryKey: ['today'],
    queryFn: () => api.get('/api/today'),
    staleTime: 60_000,
    enabled: !alreadyShown,
  })

  const feedbackMutation = useMutation({
    mutationFn: (payload: FeedbackPayload) => api.post('/api/feedback', payload),
  })

  useEffect(() => {
    if (checked.current || !data || alreadyShown) return
    checked.current = true
    if (data.days_since_registration >= RATING_DAYS_THRESHOLD) {
      const t = setTimeout(() => setShow(true), 1500)
      return () => clearTimeout(t)
    }
  }, [data, alreadyShown])

  const dismiss = () => {
    localStorage.setItem(RATING_LS_KEY, '1')
    setShow(false)
  }

  const submit = () => {
    if (!rating) return
    haptic.impactOccurred('medium')
    feedbackMutation.mutate(
      { rating, text: text.trim() || undefined },
      {
        onSettled: () => {
          localStorage.setItem(RATING_LS_KEY, '1')
          setDone(true)
        },
      },
    )
  }

  if (!show) return null

  // Через портал в body. Прежний z-[75] выглядел с запасом, но значил не
  // больше, чем z-50: он сравнивался внутри контекста наложения страницы,
  // а таб-бар живёт в корневом — и всё равно оказывался сверху.
  return createPortal(
    <div className="fixed inset-0 flex flex-col justify-end" style={{ zIndex: 75 }}>
      <div className="absolute inset-0 bg-black/40 scrim-enter" onClick={dismiss} />
      <div className="relative bg-bg-primary rounded-t-2xl px-5 pt-5 pb-8 space-y-4 sheet-enter">
        {done ? (
          <div className="py-4 text-center space-y-2">
            {rating >= 4 ? (
              <>
                <p className="text-h3 text-ink">Приятно слышать</p>
                <p className="text-sm text-ink-2">Расскажи друзьям о Delo</p>
                <button
                  onClick={() => {
                    WebApp.openTelegramLink(
                      `https://t.me/share/url?url=https://t.me/DeloAppBot&text=${encodeURIComponent(SHARE_TEXT)}`,
                    )
                    dismiss()
                  }}
                  className="btn-active mt-2 inline-flex items-center gap-2 bg-accent text-white text-sm font-medium px-5 py-2.5 rounded-lg"
                >
                  <Share2 size={14} />
                  Поделиться
                </button>
              </>
            ) : (
              <p className="text-sm text-ink-2">Спасибо — учту.</p>
            )}
          </div>
        ) : (
          <>
            <div>
              <p className="text-h3 text-ink mb-1">Как тебе Delo?</p>
              <p className="text-sm text-ink-2">Займёт 10 секунд</p>
            </div>

            <div className="flex gap-2 justify-center py-1">
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  onClick={() => { haptic.impactOccurred('light'); setRating(s) }}
                  className="btn-active p-1"
                >
                  <Star
                    size={32}
                    className={s <= rating ? 'text-warning' : 'text-ink-2'}
                    fill={s <= rating ? 'currentColor' : 'none'}
                  />
                </button>
              ))}
            </div>

            {rating > 0 && rating <= 3 && (
              <div className="space-y-2">
                <p className="text-sm text-ink-2">Жаль. Что можно улучшить?</p>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Напиши что не так..."
                  rows={3}
                  className="w-full text-base text-ink bg-surface-2 border border-line-1 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:border-accent placeholder:text-ink-2"
                />
              </div>
            )}

            {rating > 0 && (
              <button
                onClick={submit}
                disabled={feedbackMutation.isPending}
                className="btn-active w-full bg-accent text-white text-sm font-medium py-2.5 rounded-lg disabled:opacity-40"
              >
                {feedbackMutation.isPending ? 'Отправляю…' : 'Отправить'}
              </button>
            )}

            <button onClick={dismiss} className="w-full text-sm text-ink-2 py-1">
              Не сейчас
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
